export const countryNames = [
  'Australia',
  'Austria',
  'Belgium',
  'Brazil',
  'Bulgaria',
  'Canada',
  'Croatia',
  'Cyprus',
  'Czech Republic',
  'Denmark',
  'Estonia',
  'Finland',
  'France',
  'Greece',
  'Germany',
  'Hungary',
  'India',
  'Ireland',
  'Italy',
  'Japan',
  'Latvia',
  'Lithuania',
  'Luxembourg',
  'Malta',
  'Mexico',
  'Netherlands',
  'New Zealand',
  'Norway',
  'Philippines',
  'Poland',
  'Portugal',
  'Romania',
  'Russia',
  'Slovakia',
  'Slovenia',
  'South Africa',
  'Spain',
  'Sweden',
  'Switzerland',
  'Thailand',
  'Turkey',
  'United Kingdom',
  'USA',
  'Egypt',
  'Argentina',
  'Chile',
  'China',
  'Colombia',
  'Hong Kong',
  'Indonesia',
  'Israel',
  'Malaysia',
  'Peru',
  'Saudi Arabia',
  'Singapore',
  'South Korea',
  'Taiwan',
  'United Arab Emirates',
  'Vietnam',
] as const;

export type CountryName = (typeof countryNames)[number];

export const countryCodes = [
  'AU',
  'AT',
  'BE',
  'BG',
  'BR',
  'CA',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'EG',
  'ES',
  'FI',
  'GR',
  'FR',
  'HR',
  'HU',
  'IN',
  'IE',
  'IT',
  'JP',
  'LV',
  'LT',
  'LU',
  'MT',
  'MX',
  'NL',
  'NZ',
  'NO',
  'PH',
  'PL',
  'PT',
  'RO',
  'RU',
  'SK',
  'SI',
  'SE',
  'CH',
  'TH',
  'TR',
  'GB',
  'US',
  'ZA',
  'AE',
  'AR',
  'CL',
  'CN',
  'CO',
  'HK',
  'ID',
  'IL',
  'KR',
  'MY',
  'PE',
  'SA',
  'SG',
  'TW',
  'VN',
] as const;

export type CountryCode = (typeof countryCodes)[number];

export const countryCodeToNameMap: Record<CountryCode, CountryName> = {
  AU: 'Australia',
  AT: 'Austria',
  BE: 'Belgium',
  BG: 'Bulgaria',
  BR: 'Brazil',
  CA: 'Canada',
  HR: 'Croatia',
  CY: 'Cyprus',
  CZ: 'Czech Republic',
  DK: 'Denmark',
  EE: 'Estonia',
  FI: 'Finland',
  FR: 'France',
  GR: 'Greece',
  DE: 'Germany',
  HU: 'Hungary',
  IN: 'India',
  IE: 'Ireland',
  IT: 'Italy',
  JP: 'Japan',
  LV: 'Latvia',
  LT: 'Lithuania',
  LU: 'Luxembourg',
  MT: 'Malta',
  MX: 'Mexico',
  NL: 'Netherlands',
  NZ: 'New Zealand',
  NO: 'Norway',
  PH: 'Philippines',
  PL: 'Poland',
  PT: 'Portugal',
  RO: 'Romania',
  RU: 'Russia',
  SK: 'Slovakia',
  SI: 'Slovenia',
  ZA: 'South Africa',
  ES: 'Spain',
  SE: 'Sweden',
  CH: 'Switzerland',
  TH: 'Thailand',
  TR: 'Turkey',
  GB: 'United Kingdom',
  US: 'USA',
  EG: 'Egypt',
  AR: 'Argentina',
  CL: 'Chile',
  CN: 'China',
  CO: 'Colombia',
  HK: 'Hong Kong',
  ID: 'Indonesia',
  IL: 'Israel',
  KR: 'South Korea',
  MY: 'Malaysia',
  PE: 'Peru',
  SA: 'Saudi Arabia',
  SG: 'Singapore',
  TW: 'Taiwan',
  AE: 'United Arab Emirates',
  VN: 'Vietnam',
};

export const countryNameToCodeMap: Record<CountryName, CountryCode> = {
  Australia: 'AU',
  Austria: 'AT',
  Belgium: 'BE',
  Brazil: 'BR',
  Bulgaria: 'BG',
  Canada: 'CA',
  Croatia: 'HR',
  Cyprus: 'CY',
  'Czech Republic': 'CZ',
  Denmark: 'DK',
  Estonia: 'EE',
  Finland: 'FI',
  France: 'FR',
  Greece: 'GR',
  Germany: 'DE',
  Hungary: 'HU',
  India: 'IN',
  Ireland: 'IE',
  Italy: 'IT',
  Japan: 'JP',
  Latvia: 'LV',
  Lithuania: 'LT',
  Luxembourg: 'LU',
  Malta: 'MT',
  Mexico: 'MX',
  Netherlands: 'NL',
  'New Zealand': 'NZ',
  Norway: 'NO',
  Philippines: 'PH',
  Poland: 'PL',
  Portugal: 'PT',
  Romania: 'RO',
  Russia: 'RU',
  Slovakia: 'SK',
  Slovenia: 'SI',
  'South Africa': 'ZA',
  Spain: 'ES',
  Sweden: 'SE',
  Switzerland: 'CH',
  Thailand: 'TH',
  Turkey: 'TR',
  'United Kingdom': 'GB',
  USA: 'US',
  Egypt: 'EG',
  Argentina: 'AR',
  Chile: 'CL',
  China: 'CN',
  Colombia: 'CO',
  'Hong Kong': 'HK',
  Indonesia: 'ID',
  Israel: 'IL',
  Malaysia: 'MY',
  Peru: 'PE',
  'Saudi Arabia': 'SA',
  Singapore: 'SG',
  'South Korea': 'KR',
  Taiwan: 'TW',
  'United Arab Emirates': 'AE',
  Vietnam: 'VN',
};

// App Store Connect returns alpha-3 territory ids. Keep this broader territory
// mapping here so pricing services do not duplicate country data.
export const alpha3ToAlpha2: Record<string, string> = {
  AUS: 'AU',
  AUT: 'AT',
  BEL: 'BE',
  BGR: 'BG',
  BRA: 'BR',
  CAN: 'CA',
  HRV: 'HR',
  CYP: 'CY',
  CZE: 'CZ',
  DNK: 'DK',
  EST: 'EE',
  EGY: 'EG',
  ESP: 'ES',
  FIN: 'FI',
  GRC: 'GR',
  FRA: 'FR',
  DEU: 'DE',
  HUN: 'HU',
  IND: 'IN',
  IRL: 'IE',
  ITA: 'IT',
  JPN: 'JP',
  LVA: 'LV',
  LTU: 'LT',
  LUX: 'LU',
  MLT: 'MT',
  MEX: 'MX',
  NLD: 'NL',
  NZL: 'NZ',
  NOR: 'NO',
  PHL: 'PH',
  POL: 'PL',
  PRT: 'PT',
  ROU: 'RO',
  RUS: 'RU',
  SVK: 'SK',
  SVN: 'SI',
  ZAF: 'ZA',
  SWE: 'SE',
  CHE: 'CH',
  THA: 'TH',
  TUR: 'TR',
  GBR: 'GB',
  USA: 'US',
  KOR: 'KR',
  SGP: 'SG',
  CHN: 'CN',
  TWN: 'TW',
  HKG: 'HK',
  ARE: 'AE',
  SAU: 'SA',
  ISR: 'IL',
  IDN: 'ID',
  MYS: 'MY',
  VNM: 'VN',
  COL: 'CO',
  ARG: 'AR',
  CHL: 'CL',
  PER: 'PE',
  AZE: 'AZ',
  KAZ: 'KZ',
  SEN: 'SN',
  ALB: 'AL',
  ARM: 'AM',
  BEN: 'BJ',
  BHR: 'BH',
  BHS: 'BS',
  BIH: 'BA',
  BLR: 'BY',
  BRB: 'BB',
  CIV: 'CI',
  CMR: 'CM',
  GEO: 'GE',
  GHA: 'GH',
  ISL: 'IS',
  KEN: 'KE',
  KGZ: 'KG',
  KHM: 'KH',
  LAO: 'LA',
  MDA: 'MD',
  MNE: 'ME',
  MUS: 'MU',
  NGA: 'NG',
  NPL: 'NP',
  QAT: 'QA',
  SRB: 'RS',
  SUR: 'SR',
  TJK: 'TJ',
  TZA: 'TZ',
  UGA: 'UG',
  UKR: 'UA',
  UZB: 'UZ',
  XKS: 'XK',
  ZMB: 'ZM',
  ZWE: 'ZW',
};

export const alpha2ToAlpha3: Record<string, string> = Object.fromEntries(
  Object.entries(alpha3ToAlpha2).map(([a3, a2]) => [a2, a3]),
);

export const alpha2ToName: Record<string, string> = {
  ...countryCodeToNameMap,
  AZ: 'Azerbaijan',
  KZ: 'Kazakhstan',
  SN: 'Senegal',
  AL: 'Albania',
  AM: 'Armenia',
  BJ: 'Benin',
  BH: 'Bahrain',
  BS: 'Bahamas',
  BA: 'Bosnia and Herzegovina',
  BY: 'Belarus',
  BB: 'Barbados',
  CI: "Cote d'Ivoire",
  CM: 'Cameroon',
  GE: 'Georgia',
  GH: 'Ghana',
  IS: 'Iceland',
  KE: 'Kenya',
  KG: 'Kyrgyzstan',
  KH: 'Cambodia',
  LA: 'Laos',
  MD: 'Moldova',
  ME: 'Montenegro',
  MU: 'Mauritius',
  NG: 'Nigeria',
  NP: 'Nepal',
  QA: 'Qatar',
  RS: 'Serbia',
  SR: 'Suriname',
  TJ: 'Tajikistan',
  TZ: 'Tanzania',
  UG: 'Uganda',
  UA: 'Ukraine',
  UZ: 'Uzbekistan',
  XK: 'Kosovo',
  ZM: 'Zambia',
  ZW: 'Zimbabwe',
};

export const getCountryName = (countryCode: string): string => {
  return alpha2ToName[countryCode] ?? countryCode;
};

export const euCountryCodes: CountryCode[] = [
  'AT', // Austria
  'BE', // Belgium
  'BG', // Bulgaria
  'HR', // Croatia
  'CY', // Cyprus
  'CZ', // Czech Republic
  'DK', // Denmark
  'EE', // Estonia
  'FI', // Finland
  'FR', // France
  'DE', // Germany
  'GR', // Greece
  'HU', // Hungary
  'IE', // Ireland
  'IT', // Italy
  'LV', // Latvia
  'LT', // Lithuania
  'LU', // Luxembourg
  'MT', // Malta
  'NL', // Netherlands
  'PL', // Poland
  'PT', // Portugal
  'RO', // Romania
  'SK', // Slovakia
  'SI', // Slovenia
  'ES', // Spain
  'SE', // Sweden
];

export const isEuCountry = (countryCode?: string): boolean => {
  return euCountryCodes.includes(countryCode as CountryCode);
};

/** EU plus the EEA states that apply the EU consumer-contract rules (Norway, Iceland, Liechtenstein). */
export const eeaCountryCodes: string[] = [...euCountryCodes, 'NO', 'IS', 'LI'];

export const isEeaCountry = (countryCode?: string): boolean => {
  return !!countryCode && eeaCountryCodes.includes(countryCode);
};

export const countryCodeToCurrencyMap: Record<CountryCode, string> = {
  AU: 'AUD',
  AT: 'EUR',
  BE: 'EUR',
  BG: 'BGN',
  BR: 'BRL',
  CA: 'CAD',
  HR: 'HRK',
  CY: 'EUR',
  CZ: 'CZK',
  DK: 'DKK',
  EE: 'EUR',
  FI: 'EUR',
  FR: 'EUR',
  DE: 'EUR',
  GR: 'EUR',
  HU: 'HUF',
  IN: 'INR',
  IE: 'EUR',
  IT: 'EUR',
  JP: 'JPY',
  LV: 'EUR',
  LT: 'EUR',
  LU: 'EUR',
  MT: 'EUR',
  MX: 'MXN',
  NL: 'EUR',
  NZ: 'NZD',
  NO: 'NOK',
  PH: 'PHP',
  PL: 'PLN',
  PT: 'EUR',
  RO: 'RON',
  RU: 'RUB',
  SK: 'EUR',
  SI: 'EUR',
  ZA: 'ZAR',
  ES: 'EUR',
  SE: 'SEK',
  CH: 'CHF',
  TH: 'THB',
  TR: 'TRY',
  GB: 'GBP',
  US: 'USD',
  EG: 'EGP',
  AE: 'AED',
  AR: 'ARS',
  CL: 'CLP',
  CN: 'CNY',
  CO: 'COP',
  HK: 'HKD',
  ID: 'IDR',
  IL: 'ILS',
  KR: 'KRW',
  MY: 'MYR',
  PE: 'PEN',
  SA: 'SAR',
  SG: 'SGD',
  TW: 'TWD',
  VN: 'VND',
};

/** Common aliases and alternate names that map to canonical country codes. */
export const countryAliasToCodeMap: Record<string, CountryCode> = {
  UK: 'GB',
  England: 'GB',
  'United States': 'US',
  'United States of America': 'US',
  America: 'US',
  UAE: 'AE',
  Emirates: 'AE',
  Korea: 'KR',
  Philippines: 'PH',
};
